import React from 'react';
import { EventItem } from 'components/EventsCalendar/types';
import { useTranslation } from 'react-i18next';
import { MdMoreHoriz } from 'react-icons/md';
import CalendarIcon from './components/icons/CalendarIcon';
import Link from 'next/link';

interface EventCardHeaderProps {
  event: EventItem;
  isDropdownOpen: boolean;
  setIsDropdownOpen: (open: boolean) => void;
  handleReportEvent: () => void;
  handleGoToEvent: () => void;
  org: {
    name: string;
    badgeImageUrl: string;
    id: string;
  };
}

const EventCardHeader = ({
  event,
  isDropdownOpen,
  setIsDropdownOpen,
  handleReportEvent,
  handleGoToEvent,
  org
}: EventCardHeaderProps) => {
  const { t } = useTranslation(['organizations']);

  const toggleDropdown = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDropdownOpen(!isDropdownOpen);
  };

  return (
    <>
      {/* Dropdown menu positioned absolutely outside the hover container */}
      {isDropdownOpen && (
        <div className="absolute top-12 right-2 z-50 shadow bg-white h-auto whitespace-nowrap rounded-lg overflow-hidden">
          <ul className="text-left">
            <li
              className="px-3 py-2 text-gray-700 hover:bg-gray-200 cursor-pointer"
              onClick={handleGoToEvent}
            >
              <span className="text-sm">{t('events.goToEvent')}</span>
            </li>
            <li
              className="px-3 py-2 text-gray-700 hover:bg-gray-200 cursor-pointer"
              onClick={handleReportEvent}
            >
              <span className="text-sm text-red-500">
                {t('events.reportEvent')}
              </span>
            </li>
          </ul>
        </div>
      )}

      {/* Header section */}
      <div className="z-30 flex flex-row px-2 pt-2 pb-3 font-bold text-lg items-center gap-3 w-screen md:w-[360px]">
        <div
          className="w-[40px] h-[40px] bg-gray-100 rounded-lg border-2 overflow-hidden border-[#EFF2FF] hover:scale-110 hover:shadow-lg hover:brightness-110 transition-all duration-300 ease-in-out"
          style={{ boxShadow: '0px 8.02px 23.2px rgba(66, 84, 152, 0.1)' }}
        >
          <Link href={`/organizations/${org.id}`}>
            <img src={org.badgeImageUrl} alt={`${org.name}'s badge`} />
          </Link>
        </div>
        <div className="flex flex-col items-start justify-center flex-1 min-w-0">
          <h3 className="text-base font-bold truncate w-full">
            <Link href={`/organizations/${org.id}`} className="hover:underline">{`${org.name}`}</Link>
          </h3>
          <div className="flex flex-row items-center gap-1 w-full min-w-0">
            <CalendarIcon />
            <Link
              href={`/events/${event.id}`}
              className="hover:underline text-base font-normal truncate flex-1 min-w-0"
            >
              {t('events.hosting', { postProcess: 'colon' }) + ' ' + event.title}
            </Link>
          </div>
        </div>
        <button
          onClick={toggleDropdown}
          className="ml-auto relative z-30 p-1 rounded-full hover:bg-gray-200 transition-colors duration-300 ease-in-out"
        >
          <MdMoreHoriz size={24} color="black" />
        </button>
      </div>
    </>
  );
};

export default EventCardHeader;
